import React, { useState, useEffect } from "react";

import { useNavigate } from "react-router-dom";

import {
  CheckCircle as CheckIcon,
  Info as InfoIcon,
  KeyboardArrowRight as ArrowIcon,
  Calculate as CalculateIcon,
  TimerOutlined as TimerIcon,
  Group as TeamIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  Container,
  Divider,
  Grid,
  Paper,
  Slider,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";

import { apiRoot } from "../api/clusterApi";
import InterestForm from "../components/interestForm";
import { Replaces, totalPrice } from "../components/replaces";

interface Price {
  priceID: string;
  amount: number;
}

export function Pricing() {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [price, setPrice] = useState<Price>({ priceID: "", amount: 5 });
  const [teamSize, setTeamSize] = useState<number>(1);
  const [orgSize, setOrgSize] = useState<number>(50);
  const [salary, setSalary] = useState<number>(100000);
  const [toolSavings, setToolSavings] = useState<number>(totalPrice(50));

  useEffect(() => {
    const fetchPrice = async () => {
      try {
        const response = await fetch(`${apiRoot}/pricing`);
        const price = (await response.json()) as Price;
        setPrice(price);
      } catch (error) {
        console.error("Failed to fetch pricing:", error);
      }
    };
    fetchPrice();
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Format prices for different time periods
  const formatPrice = (amount: number, multiplier: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format((amount / 100) * multiplier);
  };

  const prices = {
    minute: formatPrice(price.amount, 1),
    hour: formatPrice(price.amount, 60),
    day: formatPrice(price.amount, 60 * 24),
    month: formatPrice(price.amount, 60 * 24 * 30),
    year: formatPrice(price.amount, 60 * 24 * 365),
  };

  // Format values for ROI calculator
  const formattedAvgSalary = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(salary);

  const formattedSalary = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(salary * teamSize);

  const annualPlatformCost = (price.amount / 100) * 60 * 24 * 365;

  // Using the dynamically updated toolSavings
  const annualSavings = salary * teamSize - annualPlatformCost + toolSavings;
  const formattedSavings = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(annualSavings);

  const savingsPercentage = Math.round(
    (annualSavings / (salary * teamSize + toolSavings)) * 100,
  );

  return (
    <Box sx={{ overflow: "hidden" }}>
      {/* Hero Section */}
      <Box
        sx={{
          position: "relative",
          backgroundImage:
            "radial-gradient(circle at 25% 25%, rgba(67, 211, 255, 0.1), transparent 40%), radial-gradient(circle at 75% 75%, rgba(255, 150, 67, 0.1), transparent 40%)",
          py: { xs: 8, md: 10 },
          mb: 8,
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <Container maxWidth="lg">
          <Stack spacing={4} alignItems="center" textAlign="center">
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: "2.5rem", sm: "3rem", md: "3.5rem" },
                fontWeight: 800,
                lineHeight: 1.2,
                background: "linear-gradient(90deg, #43d3ff 0%, #46ff93 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Simple, Transparent Pricing
            </Typography>
            <Typography
              variant="h5"
              color="text.secondary"
              sx={{ fontWeight: 400, maxWidth: 700 }}
            >
              Pay only for what you use, with no hidden fees or complex pricing
              tiers
            </Typography>
          </Stack>
        </Container>
      </Box>

      {/* Pricing Tabs */}
      <Container maxWidth="md" sx={{ mb: 10 }}>
        <Card
          elevation={0}
          sx={{
            borderRadius: 4,
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
            backgroundColor: "background.paper",
          }}
        >
          <Box
            sx={{
              p: 4,
              textAlign: "center",
              borderBottom: "1px solid",
              borderColor: "divider",
              backgroundImage:
                "radial-gradient(circle at 50% 0%, rgba(67, 211, 255, 0.1), transparent 50%)",
            }}
          >
            <Stack spacing={2} alignItems="center">
              <TimerIcon sx={{ fontSize: 40, color: "primary.main" }} />
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                {prices.minute}
              </Typography>
              <Typography variant="h6" color="text.secondary">
                per minute
              </Typography>
              <Box sx={{ width: "100%", maxWidth: 400, mt: 2 }}>
                <Tabs
                  value={tabValue}
                  onChange={handleTabChange}
                  variant="fullWidth"
                  sx={{
                    "& .MuiTab-root": {
                      borderRadius: 2,
                      mx: 0.5,
                      "&.Mui-selected": {
                        backgroundColor: "rgba(67, 211, 255, 0.1)",
                      },
                    },
                  }}
                >
                  <Tab label="Hourly" />
                  <Tab label="Daily" />
                  <Tab label="Monthly" />
                  <Tab label="Annual" />
                </Tabs>
              </Box>
            </Stack>
          </Box>

          <Box sx={{ p: 4, display: tabValue === 0 ? "block" : "none" }}>
            <Stack spacing={3} alignItems="center" textAlign="center">
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                {prices.hour}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Billed per hour of actual usage
              </Typography>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowIcon />}
                onClick={() => navigate("/signup")}
                sx={{
                  px: 6,
                  py: 1.5,
                  borderRadius: 2,
                  mt: 2,
                  fontWeight: 600,
                  background:
                    "linear-gradient(90deg, #43d3ff 0%, #46ff93 100%)",
                  "&:hover": {
                    background:
                      "linear-gradient(90deg, #38b8df 0%, #3be07f 100%)",
                  },
                }}
              >
                Get Started
              </Button>
            </Stack>
          </Box>

          <Box sx={{ p: 4, display: tabValue === 1 ? "block" : "none" }}>
            <Stack spacing={3} alignItems="center" textAlign="center">
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                {prices.day}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Billed per day of actual usage
              </Typography>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowIcon />}
                onClick={() => navigate("/signup")}
                sx={{
                  px: 6,
                  py: 1.5,
                  borderRadius: 2,
                  mt: 2,
                  fontWeight: 600,
                  background:
                    "linear-gradient(90deg, #43d3ff 0%, #46ff93 100%)",
                  "&:hover": {
                    background:
                      "linear-gradient(90deg, #38b8df 0%, #3be07f 100%)",
                  },
                }}
              >
                Get Started
              </Button>
            </Stack>
          </Box>

          <Box sx={{ p: 4, display: tabValue === 2 ? "block" : "none" }}>
            <Stack spacing={3} alignItems="center" textAlign="center">
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                {prices.month}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Billed per month of actual usage
              </Typography>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowIcon />}
                onClick={() => navigate("/signup")}
                sx={{
                  px: 6,
                  py: 1.5,
                  borderRadius: 2,
                  mt: 2,
                  fontWeight: 600,
                  background:
                    "linear-gradient(90deg, #43d3ff 0%, #46ff93 100%)",
                  "&:hover": {
                    background:
                      "linear-gradient(90deg, #38b8df 0%, #3be07f 100%)",
                  },
                }}
              >
                Get Started
              </Button>
            </Stack>
          </Box>

          <Box sx={{ p: 4, display: tabValue === 3 ? "block" : "none" }}>
            <Stack spacing={3} alignItems="center" textAlign="center">
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                {prices.year}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Billed per year of actual usage
              </Typography>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowIcon />}
                onClick={() => navigate("/signup")}
                sx={{
                  px: 6,
                  py: 1.5,
                  borderRadius: 2,
                  mt: 2,
                  fontWeight: 600,
                  background:
                    "linear-gradient(90deg, #43d3ff 0%, #46ff93 100%)",
                  "&:hover": {
                    background:
                      "linear-gradient(90deg, #38b8df 0%, #3be07f 100%)",
                  },
                }}
              >
                Get Started
              </Button>
            </Stack>
          </Box>

          <Divider />

          <Box sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              What's included:
            </Typography>
            <Grid container spacing={2}>
              {[
                "Unlimited clusters",
                "Access to all platform features",
                "Full observability & monitoring",
                "Security & compliance tools",
                "Automated workflows",
                "Developer portal",
                "Built-in messaging",
                "Identity & access management",
                "Certificate management",
                "24/7 Support available (additional cost)",
              ].map((feature, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CheckIcon color="primary" />
                    <Typography variant="body1">{feature}</Typography>
                  </Stack>
                </Grid>
              ))}
            </Grid>
            <Box
              sx={{
                mt: 4,
                p: 3,
                bgcolor: "rgba(67, 211, 255, 0.05)",
                borderRadius: 2,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                <InfoIcon
                  fontSize="small"
                  sx={{ verticalAlign: "middle", mr: 1 }}
                />
                Cloud infrastructure costs are handled on your own cloud
                account. Our platform is designed to be cost-efficient on most
                cloud providers.
              </Typography>
            </Box>
          </Box>
        </Card>
      </Container>

      {/* ROI Calculator */}
      <Box
        sx={{
          backgroundColor: "background.paper",
          py: 10,
          borderTop: "1px solid",
          borderBottom: "1px solid",
          borderColor: "divider",
          mb: 10,
        }}
      >
        <Container maxWidth="lg">
          <Stack spacing={6}>
            <Stack spacing={2} alignItems="center" textAlign="center">
              <CalculateIcon sx={{ fontSize: 40, color: "primary.main" }} />
              <Typography variant="h2" sx={{ fontWeight: 700 }}>
                ROI Calculator
              </Typography>
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ maxWidth: 700 }}
              >
                See how much you could save by using Microcumulus instead of
                building and maintaining your own platform
              </Typography>
            </Stack>

            <Grid container spacing={4}>
              <Grid item xs={12} md={5}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    height: "100%",
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="h5" sx={{ mb: 4, fontWeight: 600 }}>
                    Adjust your parameters
                  </Typography>

                  <Stack spacing={4}>
                    <Box>
                      <Stack
                        direction="row"
                        spacing={2}
                        alignItems="center"
                        sx={{ mb: 1 }}
                      >
                        <TeamIcon color="primary" />
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          Platform Engineers: {teamSize}
                        </Typography>
                      </Stack>
                      <Slider
                        value={teamSize}
                        onChange={(_, value) => setTeamSize(value as number)}
                        step={1}
                        marks
                        min={1}
                        max={10}
                        valueLabelDisplay="auto"
                        sx={{
                          color: "primary.main",
                          "& .MuiSlider-thumb": {
                            "&:hover, &.Mui-focusVisible": {
                              boxShadow: "0 0 0 8px rgba(67, 211, 255, 0.16)",
                            },
                          },
                        }}
                      />
                    </Box>

                    <Box>
                      <Typography
                        variant="body1"
                        sx={{ mb: 1, fontWeight: 500 }}
                      >
                        Average Engineer Salary
                      </Typography>
                      <TextField
                        fullWidth
                        variant="outlined"
                        value={salary}
                        onChange={(e) => {
                          const value = parseInt(e.target.value);
                          if (!isNaN(value)) setSalary(value);
                        }}
                        InputProps={{
                          startAdornment: "$",
                          sx: { borderRadius: 2 },
                        }}
                      />
                    </Box>

                    {/* Organization size moved to toolchain section */}
                  </Stack>
                </Paper>
              </Grid>

              <Grid item xs={12} md={7}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    height: "100%",
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      width: "60%",
                      height: "100%",
                      opacity: 0.05,
                      background:
                        "radial-gradient(circle at 80% 50%, rgba(70, 255, 147, 0.8), transparent 70%)",
                    }}
                  />

                  <Typography variant="h5" sx={{ mb: 4, fontWeight: 600 }}>
                    Your potential savings
                  </Typography>

                  <Card
                    elevation={0}
                    sx={{
                      p: 3,
                      mb: 4,
                      backgroundColor: "rgba(70, 255, 147, 0.05)",
                      borderRadius: 3,
                      border: "1px solid",
                      borderColor: "rgba(70, 255, 147, 0.2)",
                    }}
                  >
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={3}
                      alignItems={{ sm: "center" }}
                      justifyContent="space-between"
                    >
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Annual Savings
                        </Typography>
                        <Typography
                          variant="h3"
                          sx={{ mt: 1, fontWeight: 700 }}
                        >
                          {formattedSavings}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          p: 2,
                          borderRadius: "50%",
                          width: 100,
                          height: 100,
                          bgcolor: "rgba(70, 255, 147, 0.1)",
                          border: "2px solid",
                          borderColor: "rgba(70, 255, 147, 0.3)",
                        }}
                      >
                        <Typography variant="h4" sx={{ fontWeight: 700 }}>
                          {savingsPercentage}%
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          SAVINGS
                        </Typography>
                      </Box>
                    </Stack>
                  </Card>

                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <Card
                        elevation={0}
                        sx={{
                          p: 2,
                          backgroundColor: "rgba(67, 211, 255, 0.05)",
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: "rgba(67, 211, 255, 0.2)",
                          height: "100%",
                        }}
                      >
                        <Typography variant="subtitle2" color="text.secondary">
                          DIY Platform Engineering
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{ mt: 1, fontWeight: 600 }}
                        >
                          {formattedSalary}/year
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 1 }}
                        >
                          {teamSize} engineers × {formattedAvgSalary}
                        </Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Card
                        elevation={0}
                        sx={{
                          p: 2,
                          backgroundColor: "rgba(67, 211, 255, 0.05)",
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: "rgba(67, 211, 255, 0.2)",
                          height: "100%",
                        }}
                      >
                        <Typography variant="subtitle2" color="text.secondary">
                          Tool Replacement Savings
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{ mt: 1, fontWeight: 600 }}
                        >
                          {new Intl.NumberFormat("en-US", {
                            style: "currency",
                            currency: "USD",
                          }).format(toolSavings)}
                          /year
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 1 }}
                        >
                          See detailed breakdown below
                        </Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={12}>
                      <Card
                        elevation={0}
                        sx={{
                          p: 2,
                          backgroundColor: "rgba(255, 150, 67, 0.05)",
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: "rgba(255, 150, 67, 0.2)",
                        }}
                      >
                        <Typography variant="subtitle2" color="text.secondary">
                          Microcumulus Cost
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{ mt: 1, fontWeight: 600 }}
                        >
                          {prices.year}/year
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 1 }}
                        >
                          For unlimited clusters with all features
                        </Typography>
                      </Card>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          </Stack>
        </Container>
      </Box>

      {/* Tools Replacement Section */}
      <Container maxWidth="lg" sx={{ mb: 10 }}>
        <Box sx={{ textAlign: "center", mb: 6 }}>
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 2 }}>
            Replace Your Entire Toolchain
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ maxWidth: 700, mx: "auto" }}
          >
            Microcumulus replaces numerous tools, saving you both money and
            integration headaches
          </Typography>
        </Box>

        <Replaces
          orgSize={orgSize}
          setOrgSize={setOrgSize}
          onSavingsChange={setToolSavings}
        />
      </Container>

      {/* Interest Form Section */}
      <Container maxWidth="lg" sx={{ mb: 12 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 4, md: 6 },
                height: "100%",
                borderRadius: 4,
                textAlign: "center",
                background:
                  "linear-gradient(135deg, rgba(26, 30, 35, 0.95) 0%, rgba(43, 49, 55, 0.95) 100%)",
                border: "1px solid",
                borderColor: "divider",
                position: "relative",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  opacity: 0.1,
                  background:
                    "radial-gradient(circle at 70% 20%, rgba(67, 211, 255, 0.8), transparent 60%)",
                }}
              />

              <Typography variant="h3" sx={{ fontWeight: 700, mb: 3 }}>
                Start Saving Today
              </Typography>

              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ mb: 4, maxWidth: 700, mx: "auto" }}
              >
                Experience the full power of Microcumulus and see how much time
                and money you can save. Contact us at andrew@microcumul.us for a
                free trial or demo. 24/7 support options are available at additional cost.
              </Typography>

              <Button
                variant="contained"
                size="large"
                onClick={() => navigate("/signup")}
                sx={{
                  px: 6,
                  py: 1.5,
                  borderRadius: 2,
                  fontSize: "1.1rem",
                  fontWeight: 600,
                  alignSelf: "center",
                  background:
                    "linear-gradient(90deg, #43d3ff 0%, #46ff93 100%)",
                  boxShadow: "0 4px 14px 0 rgba(67, 211, 255, 0.3)",
                  "&:hover": {
                    background:
                      "linear-gradient(90deg, #38b8df 0%, #3be07f 100%)",
                    boxShadow: "0 6px 20px 0 rgba(67, 211, 255, 0.4)",
                  },
                }}
              >
                Get Started
              </Button>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <InterestForm />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
